/**
 * DeckDraftSection — the lobby's deck-builder draft surface.
 *
 * Owns three states inside the same card:
 *   • Draft Settings (host-only collapsible) — shared deck size, card pool,
 *     draft format, and "drafted deck" mode.
 *   • Pre-pick placeholder — idle (Start Draft button) and drafting
 *     (per-player Ready Up cards) variants of the Dices placeholder.
 *   • Post-draft summary — emerald card recapping the draft inputs + a
 *     "View Deck" entry into the DraftedDeckModal.
 *
 * Pure UI — all state comes in via props, all actions go out via
 * callbacks. The lobby page owns the draft state machinery.
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Check, Info, Dices } from "lucide-react";

import { SectionLabel, CollapseToggle } from "@/components/lobby/primitives";
import { RARITY_OPTIONS } from "@/constants/rarities";
import { DRAFT_FORMATS } from "@/constants/draft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeckDraftSection({
  deckSize, onDeckSize, includedPools, onTogglePool, draftPhase, disabled = false,
  // Host-only Start Draft action, rendered inside the draft placeholder box
  // below the selected-settings pills.
  isHome = false, canStartDraft = false, onStartDraft,
  // Per-player ready-up shown during the 'drafting' phase. `role` is "home"
  // or "away" — picks which side's Ready button this viewer can toggle.
  role = "home", draftReadyHome = false, draftReadyAway = false, onDraftReady,
  // Drafted-deck output mode: "shared" combines both players' picks into one
  // shared game deck; "split" (Upcoming) is per-player decks.
  draftDeckMode = "shared", onDraftDeckMode,
  // When the draft is complete, the placeholder shows a summary + "View Deck"
  // button instead of the lobby ready-up cards.
  onViewDeck,
  deckCardCount = 0,
  // True once the server has initialized the pick pool — hide the
  // placeholder + ready cards so they don't duplicate DraftPage.
  picksActive = false,
  // Optional drag handle rendered alongside the SectionLabel — only passed in
  // when both this and the Player Draft section are enabled (host reorders).
  dragHandle = null,
}) {
  // Open by default — when this section is on screen at all, the host is
  // in deck-builder mode and almost certainly wants to see/tweak the
  // settings. Still collapsible if they want to clean up the view.
  const [draftSettingsOpen, setDraftSettingsOpen] = useState(true);
  // Per-rarity card counts for the Card Pool labels ("(N)" suffix). One
  // fetch on mount — endpoint is cheap and the data doesn't change.
  const [rarityCounts, setRarityCounts] = useState({});
  useEffect(() => {
    axios.get(`${API}/cards/rarity-counts`).then(r => setRarityCounts(r.data)).catch(() => {});
  }, []);

  // Pool size validation. Pack draft burns 2 cards per pack (Deal 4 · Pick 2 · Burn 2),
  // so it needs ceil(deckSize/2)*4 eligible cards.
  const totalAvailable = includedPools.reduce((sum, k) => sum + (rarityCounts[k] || 0), 0);
  const requiredCards = Math.ceil(deckSize / 2) * 4;
  const poolInsufficient = includedPools.length > 0 && totalAvailable < requiredCards;

  // Settings lock — locked once complete, or while drafting if home has already
  // readied up. If home is in "drafting" but hasn't readied, they can still
  // tweak (unready → edit → re-ready). Away player never sees settings controls.
  const settingsLocked = draftPhase !== "idle" && !(isHome && draftPhase === "drafting" && !draftReadyHome);
  const settingsLockReason = draftPhase === "complete" ? "Draft complete — settings locked." : "Unready to change settings.";

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <SectionLabel>Deck Draft</SectionLabel>
        {dragHandle}
      </div>

      {/* ── Inner Manager Card Draft block first ──────────────────────────
          Mirrors GameSettingsPanel above (visible row → collapsed
          "Enhanced Mechanics" beneath). */}

      {/* Draft complete — summary panel + "View Deck" entry. */}
      {!picksActive && draftPhase === "complete" && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-6 py-6 text-center flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <Check size={18} className="text-emerald-400" />
            <p className="font-black text-base text-emerald-300 tracking-wide">Draft Complete</p>
          </div>
          <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
            Your {deckCardCount || deckSize}-card shared deck is ready. Build your rosters below to start the game.
          </p>
          <div className="flex flex-wrap gap-1.5 justify-center">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              {DRAFT_FORMATS[0].label} draft
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              {deckCardCount || deckSize} cards
            </span>
            {includedPools.map(k => {
              const r = RARITY_OPTIONS.find(o => o.key === k);
              const count = rarityCounts[k];
              return (
                <span key={k} className={`text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 ${r?.color}`}>
                  {r?.label}{count != null && <span className="text-slate-400 font-normal"> ({count})</span>}
                </span>
              );
            })}
          </div>
          {onViewDeck && (
            <button
              type="button"
              onClick={onViewDeck}
              className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-xs tracking-wide transition-all"
            >
              <Info size={13} />
              View Deck
            </button>
          )}
        </div>
      )}

      {/* Idle / drafting placeholder. Hidden once the server has initialized
          the pool — the dedicated DraftPage takes over above. */}
      {!picksActive && draftPhase !== "complete" && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/30 px-6 py-10 flex flex-col items-center justify-center gap-3 text-center">
          <Dices size={32} className="text-slate-700" />
          <div>
            <p className="font-black text-base text-slate-500">Manager Card Draft</p>
            <p className="text-xs text-slate-600 mt-1 max-w-xs">
              {draftPhase === "drafting"
                ? `Both players ready up to start the ${DRAFT_FORMATS[0].label.toLowerCase()} draft for the ${deckSize}-card shared deck.`
                : `Players take turns picking cards to build a shared ${deckSize}-card deck.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-center mt-1">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              {deckSize} cards
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              {DRAFT_FORMATS[0].label} draft
            </span>
            {includedPools.map(k => {
              const r = RARITY_OPTIONS.find(o => o.key === k);
              const count = rarityCounts[k];
              return (
                <span key={k} className={`text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 ${r?.color}`}>
                  {r?.label}{count != null && <span className="text-slate-400 font-normal"> ({count})</span>}
                </span>
              );
            })}
          </div>

          {/* Idle: host kicks off the draft; away sees a waiting hint. */}
          {draftPhase === "idle" && isHome && (
            <button
              type="button"
              onClick={onStartDraft}
              disabled={!canStartDraft || poolInsufficient}
              className="mt-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black text-sm tracking-wide transition-all"
            >
              {!canStartDraft ? "Select a card pool to start draft"
                : poolInsufficient ? `Need ${requiredCards} cards — only ${totalAvailable} available`
                : "Start Draft →"}
            </button>
          )}
          {draftPhase === "idle" && !isHome && (
            <span className="text-[11px] text-slate-500 font-medium">Waiting for host to start the draft…</span>
          )}

          {/* Drafting: per-player Ready cards. */}
          {draftPhase === "drafting" && (
            <div className="w-full flex gap-2 mt-2">
              {[
                { side: "home", label: "Home", ready: draftReadyHome },
                { side: "away", label: "Away", ready: draftReadyAway },
              ].map(({ side, label, ready }) => {
                const mine = role === side;
                return (
                  <div key={side}
                    className={`flex-1 rounded-xl border px-4 py-3 flex items-center justify-between gap-3
                      ${ready ? "bg-emerald-500/10 border-emerald-500/40" : "bg-slate-800/60 border-slate-700/60"}`}>
                    <div className="text-left">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
                      <p className={`text-xs font-bold ${ready ? "text-emerald-400" : "text-slate-400"}`}>
                        {ready ? "Ready to draft ✓" : "Not ready"}
                      </p>
                    </div>
                    {mine && (
                      <button
                        type="button"
                        onClick={() => onDraftReady(!ready)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black transition-colors
                          ${ready ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-amber-500 hover:bg-amber-400 text-black"}`}
                      >
                        {ready ? "Unready" : "Ready Up"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Draft Settings collapsible — host-only, below the placeholder ── */}
      {!disabled && (
        <>
          <CollapseToggle open={draftSettingsOpen} onToggle={() => setDraftSettingsOpen(o => !o)} label="Draft Settings" />
          {draftSettingsOpen && (
            <div className="space-y-4 pt-1">
              {settingsLocked && (
                <p className="text-[10px] font-bold text-amber-500/80 italic">{settingsLockReason}</p>
              )}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${settingsLocked ? "opacity-60 pointer-events-none" : ""}`}>
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Shared Deck Size
                  </label>
                  <select
                    value={deckSize}
                    onChange={e => onDeckSize(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm font-black text-center bg-slate-800/80 border border-slate-700 text-white focus:outline-none focus:border-amber-500"
                  >
                    {/* Even values only — pack draft commits exactly 2 picks
                        per pack (one home, one away), so the deck size has to
                        be even to be reachable. */}
                    {[30, 34, 38, 42, 46, 50].map(n => (
                      <option key={n} value={n}>{n} cards</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Card Pool
                    {includedPools.length > 0 && (
                      <span className={`ml-2 normal-case font-medium ${poolInsufficient ? "text-red-400" : "text-slate-600"}`}>
                        {totalAvailable} available · {requiredCards} needed
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {RARITY_OPTIONS.map(r => {
                      const active = includedPools.includes(r.key);
                      const count = rarityCounts[r.key];
                      return (
                        <button key={r.key} onClick={() => onTogglePool(r.key)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors text-sm font-medium
                            ${active ? "bg-slate-800 border-slate-600 text-white" : "bg-slate-900/40 border-slate-800 text-slate-600"}`}>
                          <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${active ? "bg-amber-500 border-amber-500" : "border-slate-700"}`}>
                            {active && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5"><path d="M1 4l3 3 5-6" stroke="black" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <span className="flex-1 text-left">
                            <span className={active ? r.color : ""}>{r.label}</span>
                            {count != null && <span className="text-[10px] text-slate-600 ml-1">({count})</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {poolInsufficient && (
                    <p className="text-[10px] text-red-400 font-bold">
                      Pack draft needs {requiredCards} cards ({deckSize}-card deck × 2 for burns). Add more rarities or reduce deck size.
                    </p>
                  )}
                </div>
                {/* Drafted Deck mode (Shared / Per-Player [Upcoming]) */}
                <div className="space-y-2 sm:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Drafted Deck <span className="normal-case font-medium text-slate-700">how picks become the game deck</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "shared", label: "Shared Deck",      blurb: "Combine both players' picks · shuffle · one deck for play.", disabled: false },
                      { id: "split",  label: "Per-Player Decks", blurb: "Each player plays from their own picks.",                    disabled: true  },
                    ].map(opt => {
                      const active = (draftDeckMode || "shared") === opt.id;
                      const lockedOut = opt.disabled;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          disabled={lockedOut}
                          onClick={() => !lockedOut && onDraftDeckMode && onDraftDeckMode(opt.id)}
                          className={`relative text-left p-3 rounded-xl border transition-all
                            ${active
                              ? "bg-amber-500/15 border-amber-500/50 text-amber-200"
                              : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500"}
                            ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <p className="text-xs font-black">{opt.label}</p>
                          <p className="text-[10px] mt-0.5 opacity-80 leading-tight">{opt.blurb}</p>
                          {opt.disabled && (
                            <span className="absolute top-1 right-1 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                              Upcoming
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
